import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {Article, ArticleLanguage} from '../model/article';

const ARTICLE_SEEDS = [
  {id: 'mandarina', slug: 'one-only-mandarina', path: '/articles/mandarina.json'},
  {id: 'capella-sydney', slug: 'capella-sydney', path: '/articles/capella.json'}
];

@Injectable({providedIn: 'root'})
export class ArticleService {
  private readonly key = 'firstclass.articles.v1';
  private seeds?: Promise<Article[]>;

  constructor(private http: HttpClient) {
  }

  async list(): Promise<Article[]> {
    this.seeds ??= Promise.all(ARTICLE_SEEDS.map(seed => firstValueFrom(this.http.get<Article>(seed.path))))
      .catch(error => {
        this.seeds = undefined;
        throw error;
      });
    const seeds = await this.seeds;
    const saved = this.read();
    return [...seeds.map(seed => saved.find(a => a.id === seed.id) ?? structuredClone(seed)),
      ...saved.filter(a => !seeds.some(seed => seed.id === a.id))];
  }

  async get(slug: string): Promise<Article | undefined> {
    return (await this.list()).find(a => a.slug === slug);
  }

  save(article: Article): void {
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(article.slug)) throw new Error('Адрес: латинские буквы, цифры и дефисы.');
    if (!article.translations.ru.title.trim() || !article.translations.en.title.trim()) throw new Error('Заполните заголовки RU и EN.');
    const saved = this.read();
    const articles = saved.filter(a => a.id !== article.id);
    if (articles.some(a => a.slug === article.slug) || ARTICLE_SEEDS.some(seed => seed.id !== article.id && seed.slug === article.slug)) {
      throw new Error('Этот адрес уже занят другой статьёй.');
    }
    try {
      localStorage.setItem(this.key, JSON.stringify([...articles, article]));
    } catch {
      throw new Error('Не удалось сохранить: память браузера недоступна или заполнена. Уменьшите фотографии или скачайте JSON.');
    }
  }

  async forHotel(hotel: { id?: number; url: string; titleEn: string; titleRu: string }): Promise<Article[]> {
    const normalize = (s: string) => s.toLowerCase().replace(/[^a-zа-я0-9]/g, '');
    return (await this.list()).filter(a => a.status === 'published' && (
      (hotel.id !== undefined && a.hotelIds.includes(hotel.id)) || a.hotelSlugs.includes(hotel.url) ||
      a.hotelNames.some(n => [hotel.titleEn, hotel.titleRu].some(t => normalize(t) === normalize(n)))
    ));
  }

  create(): Article {
    const translation = () => ({title: '', location: '', cover: {src: '', alt: ''}, blocks: []});
    return {
      schemaVersion: 1, id: crypto.randomUUID(), slug: 'article-' + Date.now(), status: 'draft',
      hotelIds: [], hotelSlugs: [], hotelNames: [], author: 'First Class | Private Travel',
      translations: {ru: translation(), en: translation()}
    };
  }

  language(code: string): ArticleLanguage {
    return code === 'en' ? 'en' : 'ru';
  }

  private read(): Article[] {
    try {
      const data: unknown = JSON.parse(localStorage.getItem(this.key) ?? '[]');
      if (!Array.isArray(data)) return [];
      return data.filter((a): a is Article => a?.schemaVersion === 1 && typeof a?.slug === 'string' &&
        Array.isArray(a.hotelIds) && Array.isArray(a.hotelSlugs) && Array.isArray(a.hotelNames) &&
        Array.isArray(a.translations?.ru?.blocks) && Array.isArray(a.translations?.en?.blocks));
    } catch {
      return [];
    }
  }
}
